import React, {useEffect, useId, useState} from 'react';
import PropTypes from 'prop-types';
import './RecordAutomaticSubtitleFile.css';
import FormElementHeader from '../form/FormElementHeader';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE_MODELS, LANGUAGES } from '../../Constants.js';
import HelpDialog from "../dialog/HelpDialog";
import CheckBox from "./CheckBox";
import Message from "./Message";
import DropDown from "../form/DropDown";

const RecordAutomaticSubtitle = ({ onChangeToLanguages, onChange, message, disabled = false, value = {}, language = '' }) => {
    const { t } = useTranslation();
    const languageModelHeaderId = useId();
    const languageHeaderId = useId();
    const empty = '';
    const allPossibleLanguages = ['fi-FI', 'sv-SE', 'en-US'];
    const [options, setOptions] = useState(
        LANGUAGES.map((lang) => ({ lang, isChecked: false }))
    );

    useEffect(() => {
        setOptions(prevOptions => prevOptions.map(option => ({ ...option, isChecked: false })));
    }, [language]);

    const handleChangeModel = (what, fieldValue) => {
        const newValue = { ...value, [what]: fieldValue };
        onChange(newValue);
    };

    const asLanguageModelOption = (language_model) => {
        if (language_model) {
            return { value: language_model, label: t(language_model) };
        }
        return { value: '', label: t('record_automatic_subtitle_default_language_model') };
    };

    const validationMessages = (type) => {
        if (message?.content) {
            return {
                content: message.content[type],
                type: 'warning'
            };
        }
    };

    const selectItem = (lang) => {
        const newValue = { ...value };
        const newOptions = options.map(option =>
            option.lang === lang ? { ...option, isChecked: !option.isChecked } : option
        );

        setOptions(newOptions);
        const selectedLanguages = newOptions
            .filter(option => option.isChecked)
            .map(option => option.lang);

        onChangeToLanguages(newValue, selectedLanguages);
    };

    const markChecked = (lang) => {
        return options?.find(option => option?.lang === lang)?.isChecked;
    };

    const checkBoxes = language
        ? LANGUAGES.filter((lang) => lang !== language)
        : [];

    return (
        <Container className="px-0 mb-3">
            <Row className="mb-3">
                <Col>
                    <HelpDialog label={t('record_automatic_subtitle_help_header_label')} >
                        {t('record_automatic_subtitle_help_header_label_content')}
                    </HelpDialog>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormElementHeader
                        id={languageModelHeaderId}
                        helpDialog={(
                            <HelpDialog label={t('record_automatic_subtitle_help_label')} >
                                {t('record_automatic_subtitle_help_label_content')}
                            </HelpDialog>
                        )}
                    >
                        {t('record_automatic_subtitle_language_model_header')}
                    </FormElementHeader>
                </Col>
            </Row>
            <Row>
                <Col>
                    <DropDown
                        aria-labelledby={languageModelHeaderId}
                        aria-required
                        value={value.translationModel}
                        onChange={(e) => handleChangeModel('translationModel', e.target.value)}
                        options={[empty, ...DEFAULT_LANGUAGE_MODELS].map(asLanguageModelOption)}
                        message={validationMessages('translationModel')}
                        disabled={disabled}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormElementHeader id={languageHeaderId}>
                        {t('record_automatic_subtitle_language_checkboxes')}
                    </FormElementHeader>
                </Col>
            </Row>
            <Row>
                {!language ? (
                    <Col>
                        <Message type="warning">
                            {t('record_automatic_subtitle_select_language_first')}
                        </Message>
                    </Col>
                ) : (
                    checkBoxes.map((lang) => (
                        <Col
                            key={lang}
                            className="record-translation-subtitle-file-col"
                        >
                            <CheckBox
                                onChange={() => selectItem(lang)}
                                checked={markChecked(lang)}
                                disabled={disabled}
                                label={t(`spoken_language_selection_${lang}`)}
                            />
                        </Col>
                    ))
                )}
            </Row>
        </Container>
    );
};

RecordAutomaticSubtitle.propTypes = {
    language: PropTypes.string,
    message: PropTypes.shape({
        content: PropTypes.object,
        type: PropTypes.oneOf(['light', 'neutral', 'warning'])
    }),
    onChange: PropTypes.func.isRequired,
    onChangeToLanguages: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default RecordAutomaticSubtitle;
